// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminPrompts.js
// Admin: the announcement AI setup (market.announcement_ai_settings) and every
// announcement AI prompt (market.announcement_prompts) as a cascade:
//   base prompt ('summary' / 'overall')  ->  sector  ->  industry group  ->  commodity
// layers ('<kind>:<level>:<value>', layered_prompts.sql), stacked general to
// specific by analyse_announcements.py; plus Thematics and Picks.
// Left: the cascade per kind (layers that exist, every GICS sector, a form for a
// new layer with GICS groups / tagged commodities suggested, company counts) and
// a company preview (which layers its next announcement gets, and the guidance as
// assembled). Right: the editor - saving never overwrites, it adds a version that
// goes live, with an optional note; History lists every version with a line diff
// against live and restores one into the editor (saved again as a new version).
// The AI Audit page opens this page with router state {name, body?, suggestion?,
// source?}: body loads a revision into the editor unsaved, suggestion pins an
// audit's suggested change beside it.
// API (admin.py, @admin_required): GET /admin/announcement-prompts (catalogue),
// /announcement-prompts/versions?name=, /announcement-prompts/cascade?ticker=,
// POST /announcement-prompts {name, body, note}. Layout and admin guard as before.
// The preview also shows the company's classification (the health check sets it after each
// announcement) with a manual override, /admin/company-classification, which always wins.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import PromptDiff from '../components/PromptDiff';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1320px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '28px', maxWidth: '820px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px' },
  card:    { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', boxShadow: 'var(--shadow)' },
  title:   { fontSize: '14px', fontWeight: 600, marginBottom: '4px', color: 'var(--text)' },
  meta:    { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' },
  cap:     { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' },
  area:    {
    width: '100%', minHeight: '460px', boxSizing: 'border-box', resize: 'vertical',
    fontFamily: "'JetBrains Mono', monospace", fontSize: '12.5px', lineHeight: 1.6,
    padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  input:   {
    width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif",
    border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  btn:     { padding: '6px 14px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif" },
  ghost:   { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  ok:      { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
  hist:    { fontSize: '12px', padding: '4px 0', display: 'flex', justifyContent: 'space-between', gap: '12px' },
  link:    { color: 'var(--accent)', cursor: 'pointer' },
  label:   { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' },
  select:  {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px',
    fontFamily: "'Space Grotesk', sans-serif", border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  pill:    { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 9px', borderRadius: '999px', fontSize: '11px', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  pre:     { whiteSpace: 'pre-wrap', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', lineHeight: 1.6, margin: 0 },
  modal:   { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  box:     { background: 'var(--surface)', borderRadius: 'calc(var(--radius) * 2)', padding: '20px', maxWidth: '900px', width: '100%', maxHeight: '85vh', overflow: 'auto' },
};

// Two columns on desktop, stacked on phones.
const CSS = `
.ap-grid { display: grid; grid-template-columns: 330px minmax(0, 1fr); gap: 20px; align-items: start; }
.ap-side { position: sticky; top: 16px; }
.ap-item:hover { background: var(--accent-faint); }
.ap-hist { display: grid; grid-template-columns: minmax(0, 220px) minmax(0, 1fr); gap: 16px; }
@media (max-width: 900px) {
  .ap-grid, .ap-hist { grid-template-columns: minmax(0, 1fr); }
  .ap-side { position: static; }
  .ap-content { padding: 20px 14px !important; }
}
`;

const KINDS  = [['summary', 'Summary'], ['overall', 'Health check'], ['other', 'Other']];
const LEVELS = [['sector', 'Sector'], ['group', 'Industry group'], ['commodity', 'Commodity']];
const OTHER  = ['thematics', 'picks', 'company_questions'];

// What each prompt is for, shown above its editor.
const BASE_HELP = {
  summary:   'Guidance for every announcement summary, its ~300-word analysis, importance (1-5), direction, positioning and financing risk. The layers below are added after it for the company\'s sector, industry group and commodity. The document, market context, history and output format are added by the code.',
  overall:   'Guidance for the running company health check. Layers are added after it as for the summary. Earlier summaries, the previous health check and key figures are added by the code.',
  thematics: 'One theme per line: the statement, then " - " and the label, e.g. "Is related to A.I. build out - A.I.". Asked in the same AI call as the summary; each announcement is tagged with every label whose statement applies (several allowed). Labels show on the site and in Discord alerts, and the Discord Bot page\'s Thematic column sends them.',
  picks:     'Guidance for the daily AI Picks (also editable on the AI Picks page).',
  company_questions: 'Response style for Woody Bot on the company page. This applies to new questions only, independently of announcement summaries. Identity and source-integrity rules remain fixed in code.',
};
const LEVEL_HELP = {
  sector:    v => `companies in the ${v} sector. A company with a mineral commodity gets the Materials layer and one with an energy commodity the Energy layer, whatever its GICS sector.`,
  group:     v => `companies in the ${v} industry group (only while the company is routed to its own GICS sector).`,
  commodity: v => `companies whose main commodity is ${v} - the most-tagged in their last 12 months of announcements, else their company tag.`,
};
const LABEL = Object.fromEntries([...KINDS, ...LEVELS, ['thematics', 'Thematics'], ['picks', 'AI Picks'], ['company_questions', 'Company Q&A — Woody Bot'], ['base', 'Base prompt']]);

const parse = name => {
  const [kind, level, ...rest] = name.split(':');
  return level ? { kind, level, value: rest.join(':') } : { kind: name, level: 'base', value: null };
};
const help = name => {
  const p = parse(name);
  return p.level === 'base' ? BASE_HELP[p.kind]
    : `Added after the base ${LABEL[p.kind].toLowerCase()} prompt for ${LEVEL_HELP[p.level](p.value)}`;
};
const when = iso => new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const short = iso => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
const words = t => (t.trim().match(/\S+/g) || []).length;
const sameTime = (a, b) => a && b && new Date(a).getTime() === new Date(b).getTime();
const dot = on => <span style={{ width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, background: on ? 'var(--success)' : 'var(--border)' }} />;

// AI model and PDF method. Choices come from the API (admin.py), so the page
// can only offer setups the analysis code supports.
function AiSetup({ data, onSaved }) {
  const live = data.history[0];
  const [model, setModel]   = useState(live?.model || '');
  const [method, setMethod] = useState(live?.pdf_method || '');
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');

  useEffect(() => { setModel(live?.model || ''); setMethod(live?.pdf_method || ''); }, [live]);

  const native  = data.native_models.includes(model);
  const label   = (list, v) => list.find(o => o.value === v)?.label || v;
  const changed = model !== live?.model || method !== live?.pdf_method;

  // A model that can't read PDFs always uses Moonshot extraction.
  const pickModel = v => {
    setModel(v);
    if (!data.native_models.includes(v)) setMethod('moonshot');
  };

  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api.post('/admin/announcement-ai-settings', { model, pdf_method: method });
      setMsg(`${r.data.message} (${when(r.data.created_at)}).`);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.section}>
      <div style={s.title}>AI model</div>
      <div style={s.meta}>
        Used for new summaries and health checks. Applies to the next announcement - no restart needed.
        The PDF method applies to ASX only; SEC filings are always converted to text locally.<br />
        {live ? `Live since ${when(live.created_at)}${live.created_by ? ` by ${live.created_by}` : ''}` : 'No setting saved yet.'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={s.label}>Model</div>
          <select style={s.select} value={model} onChange={e => pickModel(e.target.value)}>
            {data.models.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>PDF method</div>
          <select style={s.select} value={method} onChange={e => setMethod(e.target.value)}>
            {data.pdf_methods.map(o => (
              <option key={o.value} value={o.value} disabled={o.value === 'native' && !native}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button onClick={save} disabled={!changed || busy}
                style={{ ...s.btn, background: changed ? 'var(--accent)' : 'var(--border)', color: changed ? 'var(--on-accent)' : 'var(--text-muted)', cursor: changed ? 'pointer' : 'default' }}>
          {busy ? 'Saving…' : 'Save and make live'}
        </button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}

      {data.history.length > 1 && (
        <details style={{ marginTop: '16px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)' }}>
            Earlier settings ({data.history.length - 1})
          </summary>
          {data.history.slice(1).map(v => (
            <div key={v.created_at} style={s.hist}>
              <span>{when(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ''}</span>
              <span>{label(data.models, v.model)} · {label(data.pdf_methods, v.pdf_method)}</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

// One row in the cascade list.
function Item({ name, label, sub, live, selected, onPick, indent = 0 }) {
  return (
    <div className="ap-item" onClick={() => onPick(name)}
         style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', paddingLeft: `${10 + indent * 14}px`,
                  borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '13px',
                  background: selected ? 'var(--accent-dim)' : undefined, color: live ? 'var(--text)' : 'var(--text-muted)' }}>
      {dot(!!live)}
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selected ? 600 : 400 }}>{label}</span>
      {sub && <span style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{sub}</span>}
    </div>
  );
}

// Left column: the cascade of one kind, new-layer form, company preview.
function Cascade({ cat, kind, setKind, selected, onPick, onPreview }) {
  const byName = useMemo(() => Object.fromEntries(cat.prompts.map(p => [p.name, p])), [cat]);
  const [adding, setAdding] = useState(false);
  const [level, setLevel]   = useState('commodity');
  const [value, setValue]   = useState('');

  const layers = lv => cat.prompts.filter(p => p.name.startsWith(`${kind}:${lv}:`)).map(p => parse(p.name).value);
  const count  = (lv, v) => cat.keys[lv]?.find(k => k.value === v)?.n;
  const sub    = name => {
    const used = cat.usage[name];
    return byName[name] ? `${used ? `${used} used · ` : ''}v${byName[name].versions}` : 'none';
  };
  const create = () => {
    const v = value.trim();
    if (!v) return;
    onPick(`${kind}:${level}:${v}`);
    setAdding(false); setValue('');
  };

  return (
    <div className="ap-side">
      <div style={{ ...s.card, padding: '14px' }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg)', padding: '3px', borderRadius: 'var(--radius)', marginBottom: '12px' }}>
          {KINDS.map(([k, label]) => (
            <button key={k} onClick={() => setKind(k)}
                    style={{ ...s.btn, flex: 1, padding: '6px 4px', background: kind === k ? 'var(--surface)' : 'transparent',
                             color: kind === k ? 'var(--text)' : 'var(--text-muted)', boxShadow: kind === k ? 'var(--shadow)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>

        {kind === 'other' ? OTHER.map(n => (
          <Item key={n} name={n} label={LABEL[n]} sub={byName[n] ? `v${byName[n].versions}` : 'none'}
                live={byName[n]?.live} selected={selected === n} onPick={onPick} />
        )) : (
          <>
            <Item name={kind} label="Base prompt" sub={sub(kind)} live={byName[kind]?.live} selected={selected === kind} onPick={onPick} />
            {LEVELS.map(([lv, label]) => {
              // Sectors: every GICS sector, so gaps show. Groups / commodities: the layers that exist.
              const vals = lv === 'sector'
                ? [...new Set([...cat.keys.sector.map(k => k.value), ...layers(lv)])].sort()
                : layers(lv).sort();
              return (
                <div key={lv} style={{ marginTop: '10px' }}>
                  <div style={{ ...s.cap, padding: '0 10px 4px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{label} layers</span><span>{layers(lv).length}</span>
                  </div>
                  {vals.map(v => {
                    const n = `${kind}:${lv}:${v}`, c = count(lv, v);
                    return <Item key={n} name={n} indent={1} live={byName[n]?.live} selected={selected === n} onPick={onPick}
                                 label={v} sub={byName[n] ? sub(n) : c ? `${c} cos` : 'none'} />;
                  })}
                  {!vals.length && <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '2px 24px' }}>none yet</div>}
                </div>
              );
            })}

            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
              {!adding ? (
                <button style={{ ...s.btn, ...s.ghost, width: '100%' }} onClick={() => setAdding(true)}>+ New layer</button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <select style={s.select} value={level} onChange={e => { setLevel(e.target.value); setValue(''); }}>
                    {LEVELS.map(([lv, label]) => <option key={lv} value={lv}>{label}</option>)}
                  </select>
                  <input style={s.input} list="ap-keys" value={value} onChange={e => setValue(e.target.value)}
                         placeholder={level === 'commodity' ? 'e.g. silver' : level === 'group' ? 'e.g. Banks' : 'e.g. Utilities'}
                         onKeyDown={e => e.key === 'Enter' && create()} autoFocus />
                  <datalist id="ap-keys">
                    {(cat.keys[level] || []).filter(k => !byName[`${kind}:${level}:${k.value}`]).map(k => (
                      <option key={k.value} value={k.value}>{`${k.n} companies${k.sector ? ` · ${k.sector}` : ''}`}</option>
                    ))}
                  </datalist>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={{ ...s.btn, background: 'var(--accent)', color: 'var(--on-accent)', flex: 1 }} onClick={create}>Open editor</button>
                    <button style={{ ...s.btn, ...s.ghost }} onClick={() => setAdding(false)}>Cancel</button>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Names must match exactly how companies are tagged - pick from the list. Nothing is live until you save.
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {kind !== 'other' && <Preview kind={kind} byName={byName} onPick={onPick} onAssembled={onPreview} commodities={cat.keys.commodity} />}
    </div>
  );
}

// Which prompts a company's next announcement would get (prompt_cascade.py, as of now).
// Classification: set by the health check after each announcement; a manual override
// (POST/DELETE /admin/company-classification) wins for every announcement, old and new.
const GICS = ['Communication Services', 'Consumer Discretionary', 'Consumer Staples', 'Energy', 'Financials',
              'Health Care', 'Industrials', 'Information Technology', 'Materials', 'Real Estate', 'Utilities'];

function Override({ res, commodities, onDone }) {
  const c = res.classification;
  const [sector, setSector] = useState(c?.sector || '');
  const [comm, setComm]     = useState(c?.commodity || '');
  const [err, setErr]       = useState('');
  const send = async method => {
    setErr('');
    try {
      await api.request({ url: '/admin/company-classification', method,
                          data: { instrument_id: res.instrument_id, sector: sector || null, commodity: comm || null } });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '6px 0 10px' }}>
      <select style={s.select} value={sector} onChange={e => setSector(e.target.value)}>
        <option value="">Sector - from GICS / commodity</option>
        {GICS.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
      <input style={s.input} list="ap-comm" value={comm} onChange={e => setComm(e.target.value)} placeholder="Main commodity (blank = none)" />
      <datalist id="ap-comm">{commodities.map(k => <option key={k.value} value={k.value} />)}</datalist>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button style={{ ...s.btn, background: 'var(--accent)', color: 'var(--on-accent)', flex: 1 }} onClick={() => send('POST')}>Save override</button>
        {c?.source === 'manual' && <button style={{ ...s.btn, ...s.ghost }} onClick={() => send('DELETE')}>Remove</button>}
      </div>
      {err && <div style={s.err}>✕ {err}</div>}
    </div>
  );
}

function Preview({ kind, byName, onPick, onAssembled, commodities }) {
  const [ticker, setTicker] = useState('');
  const [res, setRes]       = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [edit, setEdit]     = useState(false);

  const run = async () => {
    if (!ticker.trim()) return;
    setBusy(true); setErr(''); setRes(null); setEdit(false);
    try {
      const r = await api.get(`/admin/announcement-prompts/cascade?ticker=${encodeURIComponent(ticker.trim())}`);
      setRes(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };
  const chain = res?.chain[kind] || [];

  return (
    <div style={{ ...s.card, padding: '14px', marginTop: '16px' }}>
      <div style={{ ...s.cap, marginBottom: '8px' }}>Preview a company</div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input style={s.input} value={ticker} onChange={e => setTicker(e.target.value)} placeholder="e.g. BHP:AU or AAPL:US"
               onKeyDown={e => e.key === 'Enter' && run()} />
        <button style={{ ...s.btn, background: 'var(--accent)', color: 'var(--on-accent)' }} onClick={run} disabled={busy}>{busy ? '…' : 'Go'}</button>
      </div>
      {err && <div style={s.err}>✕ {err}</div>}
      {res && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{res.ticker} · {res.name}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            GICS: {[res.gics_sector, res.gics_group].filter(Boolean).join(' › ') || 'unknown'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: edit ? 0 : '10px' }}>
            Classified: {res.classification
              ? <>{res.classification.sector || '-'} / {res.classification.commodity || '-'} ({res.classification.source === 'manual'
                  ? 'manual override' : `health check ${short(res.classification.as_of)}`})</>
              : 'not yet - GICS and company tags'}
            {' · '}<span style={s.link} onClick={() => setEdit(!edit)}>{edit ? 'cancel' : 'override'}</span>
          </div>
          {edit && <Override res={res} commodities={commodities} onDone={run} />}
          {chain.map((c, i) => (
            <div key={c.name} style={{ display: 'flex', gap: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '12px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', marginTop: '5px', flexShrink: 0,
                               background: c.live ? 'var(--accent)' : 'var(--surface)', border: `2px solid ${c.live ? 'var(--accent)' : 'var(--border)'}` }} />
                {i < chain.length - 1 && <span style={{ flex: 1, width: '2px', background: 'var(--border)', minHeight: '14px' }} />}
              </div>
              <div style={{ paddingBottom: '8px', fontSize: '12px', cursor: 'pointer' }} onClick={() => onPick(c.name)}>
                <div style={{ color: 'var(--text-muted)' }}>{LABEL[c.level]}</div>
                <div style={{ color: c.live ? 'var(--text)' : 'var(--text-muted)' }}>
                  {c.value || LABEL[kind]}{' '}
                  {c.live ? <span style={{ color: 'var(--text-muted)' }}>· live {short(c.live)}</span>
                          : <span style={{ color: 'var(--accent)' }}>· no layer - create</span>}
                </div>
              </div>
            </div>
          ))}
          <button style={{ ...s.btn, ...s.ghost, width: '100%', marginTop: '4px' }}
                  onClick={() => onAssembled(chain.filter(c => c.live && byName[c.name]))}>
            View guidance as sent
          </button>
        </div>
      )}
    </div>
  );
}

// The base prompt and layers joined as analyse_announcements.layer_prompts does.
function Assembled({ chain, onClose }) {
  const [text, setText] = useState(null);
  useEffect(() => {
    Promise.all(chain.map(c => api.get(`/admin/announcement-prompts/versions?name=${encodeURIComponent(c.name)}`)))
      .then(rs => setText(rs.map((r, i) => {
        const c = chain[i], body = (r.data[0]?.body || '').trim();
        return c.level === 'base' ? body : `${c.level.toUpperCase()} GUIDANCE - ${c.value}\n${body}`;
      }).join('\n\n')))
      .catch(e => setText(`Could not load: ${e.message}`));
  }, [chain]);
  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.box} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <strong style={{ fontSize: '14px' }}>Guidance as sent</strong>
          <span style={s.link} onClick={onClose}>Close ✕</span>
        </div>
        <div style={{ ...s.meta }}>The live base prompt and layers, joined in order. The output format, document, market context and history are added by the code.</div>
        {text === null ? <div style={s.meta}>Loading…</div>
          : <pre style={{ ...s.pre, padding: '14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>{text}</pre>}
      </div>
    </div>
  );
}

// Right column: one prompt - edit, save, history with diffs.
function Editor({ name, meta, usage, usageDays, seed, onSaved, onDirty }) {
  const p = parse(name);
  const [versions, setVersions] = useState(null);
  const [text, setText]   = useState('');
  const [note, setNote]   = useState('');
  const [tab, setTab]     = useState('edit');
  const [pick, setPick]   = useState(0);           // history: version shown
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState('');
  const [err, setErr]     = useState('');
  const [tip, setTip]     = useState(null);        // audit suggestion pinned beside the editor

  const load = useCallback(async keep => {
    setErr('');
    try {
      const r = await api.get(`/admin/announcement-prompts/versions?name=${encodeURIComponent(name)}`);
      setVersions(r.data);
      if (!keep) setText(r.data[0]?.body || '');
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, [name]);

  useEffect(() => {
    setVersions(null); setTab('edit'); setPick(0); setMsg(''); setNote('');
    setTip(seed?.suggestion ? seed : null);
    if (seed?.body) { setText(seed.body); setNote(seed.source ? `From ${seed.source}` : ''); load(true); }
    else load(false);
  }, [name, seed, load]);

  const live    = versions?.[0];
  const changed = versions !== null && text.trim() !== (live?.body || '').trim();
  useEffect(() => { onDirty(changed); }, [changed, onDirty]);

  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api.post('/admin/announcement-prompts', { name, body: text, note });
      setMsg(`Saved - version ${when(r.data.created_at)} is live.`);
      setNote('');
      await load(false);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const crumbs = p.level === 'base' ? [LABEL[p.kind] || p.kind, 'Base prompt'] : [LABEL[p.kind], LABEL[p.level], p.value];
  const shown  = versions?.[pick];

  return (
    <div id="ap-editor" style={{ ...s.card, padding: '22px 24px', minWidth: 0 }}>
      <div style={{ ...s.cap, marginBottom: '4px' }}>{crumbs.join('  ›  ')}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <div style={{ fontSize: '18px', fontWeight: 600, letterSpacing: '-0.01em' }}>{p.value || LABEL[p.kind] || p.kind}</div>
        <code style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{name}</code>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {meta ? <>
          <span style={{ ...s.pill, color: 'var(--success)', borderColor: 'var(--success-dim)', background: 'var(--success-dim)' }}>{dot(true)} Live</span>
          <span style={s.pill}>{when(meta.live)}{meta.created_by ? ` · ${meta.created_by}` : ''}</span>
          <span style={s.pill}>{meta.versions} version{meta.versions > 1 ? 's' : ''}</span>
          {p.level !== 'base' && <span style={s.pill}>{usage || 0} summaries in {usageDays} days</span>}
        </> : <span style={{ ...s.pill, color: 'var(--accent)', background: 'var(--accent-faint)', borderColor: 'var(--accent-dim)' }}>New - not live until saved</span>}
      </div>
      <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginBottom: '16px', maxWidth: '760px' }}>{help(name)}</div>

      <div style={{ display: 'flex', gap: '18px', borderBottom: '1px solid var(--border)', marginBottom: '14px' }}>
        {[['edit', 'Edit'], ['history', `History${versions ? ` (${versions.length})` : ''}`]].map(([k, label]) => (
          <div key={k} onClick={() => setTab(k)}
               style={{ padding: '6px 0', fontSize: '13px', cursor: 'pointer', marginBottom: '-1px',
                        fontWeight: tab === k ? 600 : 400, color: tab === k ? 'var(--text)' : 'var(--text-muted)',
                        borderBottom: `2px solid ${tab === k ? 'var(--accent)' : 'transparent'}` }}>{label}</div>
        ))}
      </div>

      {tab === 'edit' && (
        <>
          {tip && (
            <div style={{ border: '1px solid var(--accent-dim)', background: 'var(--accent-faint)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
                <span style={{ ...s.cap, color: 'var(--accent)' }}>Suggested change{tip.source ? ` · ${tip.source}` : ''}</span>
                <span style={{ ...s.link, fontSize: '12px' }} onClick={() => setTip(null)}>Dismiss</span>
              </div>
              <pre style={s.pre}>{tip.suggestion}</pre>
            </div>
          )}
          {versions === null ? <div style={s.meta}>Loading…</div> : (
            <textarea style={s.area} value={text} onChange={e => setText(e.target.value)} spellCheck={false}
                      placeholder={meta ? '' : 'Write the guidance for this layer…'} />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', margin: '4px 2px 12px' }}>
            <span>{words(text).toLocaleString()} words · {text.length.toLocaleString()} characters</span>
            {changed && <span style={{ color: 'var(--accent)' }}>● Unsaved changes</span>}
          </div>
          {changed && live && (
            <details style={{ marginBottom: '12px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)' }}>Changes against live</summary>
              <div style={{ marginTop: '8px' }}><PromptDiff from={live.body} to={text} maxHeight="300px" /></div>
            </details>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input style={{ ...s.input, flex: '1 1 260px', width: 'auto' }} value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Note for the history - what changed and why (optional)" />
            <button onClick={save} disabled={!changed || busy || !text.trim()}
                    style={{ ...s.btn, padding: '8px 16px', background: changed ? 'var(--accent)' : 'var(--border)', color: changed ? 'var(--on-accent)' : 'var(--text-muted)', cursor: changed ? 'pointer' : 'default' }}>
              {busy ? 'Saving…' : meta ? 'Save as new version' : 'Create and make live'}
            </button>
            {changed && <button style={{ ...s.btn, ...s.ghost, padding: '8px 14px' }} onClick={() => setText(live?.body || '')}>Discard</button>}
          </div>
          {msg && <div style={s.ok}>✓ {msg}</div>}
          {err && <div style={s.err}>✕ {err}</div>}
        </>
      )}

      {tab === 'history' && (versions?.length ? (
        <div className="ap-hist">
          <div style={{ maxHeight: '560px', overflow: 'auto' }}>
            {versions.map((v, i) => (
              <div key={v.created_at} className="ap-item" onClick={() => setPick(i)}
                   style={{ padding: '8px 10px', borderRadius: 'var(--radius)', cursor: 'pointer', marginBottom: '2px',
                            background: pick === i ? 'var(--accent-dim)' : undefined, borderLeft: `2px solid ${i === 0 ? 'var(--success)' : 'transparent'}` }}>
                <div style={{ fontSize: '12px', fontWeight: pick === i ? 600 : 500 }}>
                  v{versions.length - i}{i === 0 && <span style={{ color: 'var(--success)', fontWeight: 500 }}> · live</span>}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{when(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ''}</div>
                {v.note && <div style={{ fontSize: '11px', color: 'var(--text)', marginTop: '2px' }}>{v.note}</div>}
              </div>
            ))}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {pick === versions.length - 1 ? 'First version' : `Changes from v${versions.length - pick - 1} to v${versions.length - pick}`}
              </span>
              {pick > 0 && (
                <button style={{ ...s.btn, background: 'var(--accent-dim)', color: 'var(--accent)' }}
                        onClick={() => { setText(shown.body); setNote(`Restored v${versions.length - pick}`); setTab('edit'); }}>
                  Restore into editor
                </button>
              )}
            </div>
            {pick === versions.length - 1
              ? <pre style={{ ...s.pre, padding: '12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: '520px', overflow: 'auto' }}>{shown.body}</pre>
              : <PromptDiff from={versions[pick + 1].body} to={shown.body} maxHeight="520px" />}
          </div>
        </div>
      ) : <div style={s.meta}>No versions yet.</div>)}
    </div>
  );
}

export default function AdminPrompts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cat, setCat]       = useState(null);
  const [ai, setAi]         = useState(null);
  const [err, setErr]       = useState('');
  const [kind, setKind]     = useState('summary');
  const [selected, setSel]  = useState('summary');
  const [seed, setSeed]     = useState(null);       // from the AI Audit page
  const [dirty, setDirty]   = useState(false);
  const [assembled, setAssembled] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/announcement-prompts');
      setCat(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);
  // Loaded separately so saving the AI setup never resets unsaved prompt edits.
  const loadAi = useCallback(async () => {
    try {
      const r = await api.get('/admin/announcement-ai-settings');
      setAi(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);
  useEffect(() => { load(); loadAi(); }, [load, loadAi]);

  // Opened from the AI Audit page: go to that prompt, once.
  useEffect(() => {
    const st = location.state;
    if (!st?.name) return;
    const p = parse(st.name);
    setKind(p.level === 'base' && OTHER.includes(p.kind) ? 'other' : p.kind);
    setSel(st.name);
    setSeed({ body: st.body, suggestion: st.suggestion, source: st.source });
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  const pick = name => {
    if (name === selected) return;
    if (dirty && !window.confirm('Discard unsaved changes to this prompt?')) return;
    setSeed(null); setSel(name);
    // Phones: the editor is below the list.
    if (window.innerWidth <= 900) setTimeout(() => document.getElementById('ap-editor')?.scrollIntoView({ behavior: 'smooth' }), 50);
  };
  const switchKind = k => {
    setKind(k);
    const p = parse(selected);
    if (k !== (OTHER.includes(p.kind) ? 'other' : p.kind)) pick(k === 'other' ? 'thematics' : k);
  };
  const onDirty = useCallback(d => setDirty(d), []);
  const meta = cat?.prompts.find(p => p.name === selected);

  return (
    <div style={s.page}>
      <style>{CSS}</style>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />
      <div className="ap-content" style={s.content}>
        <h1 style={s.heading}>AI Prompts</h1>
        <p style={s.sub}>
          Edit announcement guidance and the separate Woody Bot company Q&A style prompt. Announcement base prompts
          can have sector, industry-group and commodity layers. Saving adds a new version and makes it live for subsequent
          AI requests; previous versions remain in history.
        </p>
        {err && <div style={s.err}>✕ {err}</div>}
        {ai && <AiSetup data={ai} onSaved={loadAi} />}
        {!cat && !err && <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>}
        {cat && (
          <div className="ap-grid">
            <Cascade cat={cat} kind={kind} setKind={switchKind} selected={selected} onPick={pick} onPreview={setAssembled} />
            <Editor name={selected} meta={meta} usage={cat.usage[selected]} usageDays={cat.usage_days}
                    seed={seed} onSaved={load} onDirty={onDirty} />
          </div>
        )}
        {assembled && <Assembled chain={assembled} onClose={() => setAssembled(null)} />}
      </div>
    </div>
  );
}
